// All bullet lists are extracted to module-scope constants so React doesn't
// re-allocate them on every render.

const USER_INFO_LIST = [
    "Full name",
    "Email address",
    "Phone number / WhatsApp number",
    "Business information",
    "Profile details",
    "Payment and subscription information",
];

const SCAN_INFO_LIST = [
    "Name",
    "Contact number",
    "Email address (if submitted)",
    "Message content",
    "Date and time of interaction",
    "Device / browser information",
    "IP address",
];

const AUTOMATIC_INFO_LIST = [
    "Browser type",
    "Device type",
    "Operating system",
    "Usage analytics",
    "Cookies and session data",
];

const USAGE_PURPOSES_LIST = [
    "Provide and improve our services",
    "Generate QR code experiences",
    "Facilitate WhatsApp connections",
    "Store and manage leads",
    "Provide dashboards and analytics",
    "Process subscriptions and payments",
    "Communicate with users",
    "Prevent fraud and misuse",
    "Comply with legal obligations",
];

const SHARING_PARTIES_LIST = [
    "Payment gateway providers",
    "Cloud hosting providers",
    "Analytics providers",
    "Legal authorities when required by law",
    "Service providers necessary for platform operations",
];

const USER_PROHIBITIONS_LIST = [
    "Use the platform for illegal activities",
    "Spam or harass individuals",
    "Misuse collected lead data",
    "Upload harmful or malicious content",
    "Violate privacy or communication laws",
];

const COOKIE_USES_LIST = [
    "Improve user experience",
    "Analyze traffic and usage",
    "Maintain login sessions",
    "Personalize services",
];

const THIRD_PARTY_SERVICES_LIST = ["WhatsApp", "Razorpay", "Stripe", "Google Analytics"];

const DATA_RETENTION_REASONS_LIST = [
    "Provide services",
    "Maintain legal compliance",
    "Resolve disputes",
    "Enforce agreements",
];

const LIABILITY_EXCLUSIONS_LIST = [
    "Loss of leads or business opportunities",
    "Third-party platform failures",
    "WhatsApp outages or restrictions",
    "Unauthorized account access caused by user negligence",
];

export default function Privacy() {
    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient" data-testid="privacy-page">
            <div className="max-w-3xl mx-auto px-6">
                <div className="mb-10">
                    <span className="inline-block px-4 py-2 rounded-full glass-light text-xs font-semibold text-brand-400 mb-4">LEGAL</span>
                    <h1 className="font-display font-bold text-4xl mb-3">Privacy Policy</h1>
                    <p className="text-gray-500 text-sm">Effective Date: May 28, 2026</p>
                </div>

                <div className="glass rounded-3xl p-8 md:p-10 space-y-8 text-gray-300 text-[15px] leading-relaxed">
                    <p>
                        Welcome to <strong>Connexa</strong> (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our website, platform, QR services, and related services. By using our platform, you agree to the practices described in this Privacy Policy.
                    </p>

                    <Section title="1. Information We Collect">
                        <h4 className="font-semibold text-white mt-3 mb-2">A. Information Provided by Users</h4>
                        <p>When users register or use our services, we may collect:</p>
                        <List items={USER_INFO_LIST} />

                        <h4 className="font-semibold text-white mt-5 mb-2">B. Information Collected from QR Scans</h4>
                        <p>When someone scans a QR code generated through our platform, we may collect:</p>
                        <List items={SCAN_INFO_LIST} />

                        <h4 className="font-semibold text-white mt-5 mb-2">C. Automatically Collected Information</h4>
                        <p>We may automatically collect:</p>
                        <List items={AUTOMATIC_INFO_LIST} />
                    </Section>

                    <Section title="2. How We Use Information">
                        <p>We use collected information to:</p>
                        <List items={USAGE_PURPOSES_LIST} />
                    </Section>

                    <Section title="3. WhatsApp Integration">
                        <p>
                            Our platform may redirect users to WhatsApp using click-to-chat functionality. We are not affiliated with or endorsed by WhatsApp or Meta unless explicitly stated. Users are responsible for ensuring that messages and communications comply with applicable laws and WhatsApp policies.
                        </p>
                    </Section>

                    <Section title="4. Sharing of Information">
                        <p>We do not sell personal information. We may share information with:</p>
                        <List items={SHARING_PARTIES_LIST} />
                    </Section>

                    <Section title="5. Data Storage and Security">
                        <p>
                            We implement reasonable technical and organizational measures to protect user data. However, no online platform can guarantee absolute security. Users are responsible for maintaining the confidentiality of their account credentials.
                        </p>
                    </Section>

                    <Section title="6. User Responsibilities">
                        <p>Users agree not to:</p>
                        <List items={USER_PROHIBITIONS_LIST} />
                        <p className="mt-3">Users are solely responsible for the content of messages sent through WhatsApp or other communication platforms.</p>
                    </Section>

                    <Section title="7. Cookies and Tracking">
                        <p>We may use cookies and similar technologies to:</p>
                        <List items={COOKIE_USES_LIST} />
                        <p className="mt-3">Users may disable cookies through browser settings.</p>
                    </Section>

                    <Section title="8. Third-Party Services">
                        <p>Our platform may integrate with third-party services including:</p>
                        <List items={THIRD_PARTY_SERVICES_LIST} />
                        <p className="mt-3">We are not responsible for the privacy practices of third-party platforms. Users should review the privacy policies of those services separately.</p>
                    </Section>

                    <Section title="9. Subscription and Payment Information">
                        <p>
                            Payments may be processed through third-party payment providers. We do not directly store complete debit / credit card information on our servers. Subscription plans, billing cycles, and pricing are subject to change.
                        </p>
                    </Section>

                    <Section title="10. Data Retention">
                        <p>We retain information as long as necessary to:</p>
                        <List items={DATA_RETENTION_REASONS_LIST} />
                        <p className="mt-3">Users may request account deletion subject to legal and operational obligations.</p>
                    </Section>

                    <Section title="11. Children's Privacy">
                        <p>Our services are not intended for users under the age of 18. We do not knowingly collect personal data from minors.</p>
                    </Section>

                    <Section title="12. Limitation of Liability">
                        <p>We are not responsible for:</p>
                        <List items={LIABILITY_EXCLUSIONS_LIST} />
                        <p className="mt-3">Users use the platform at their own risk.</p>
                    </Section>

                    <Section title="13. Changes to Privacy Policy">
                        <p>
                            We reserve the right to modify this Privacy Policy at any time. Updated versions will be posted on this page with revised effective dates. Continued use of the platform constitutes acceptance of updated policies.
                        </p>
                    </Section>

                    <Section title="14. Contact Us">
                        <p>For privacy-related questions or concerns, contact:</p>
                        <div className="bg-white/5 rounded-xl p-4 mt-3 text-sm">
                            <div><strong className="text-white">Connexa Support</strong></div>
                            <div className="text-gray-400">Email: <a href="mailto:Support@connexa.co.in" className="text-brand-400 hover:underline">Support@connexa.co.in</a></div>
                            <div className="text-gray-400">Phone: <a href="tel:+919136581868" className="text-brand-400 hover:underline">+91 91365 81868</a></div>
                            <div className="text-gray-400">Website: <a href="https://www.connexa.in" target="_blank" rel="noreferrer" className="text-brand-400 hover:underline">www.connexa.in</a></div>
                        </div>
                    </Section>
                </div>
            </div>
        </section>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <h2 className="font-display font-bold text-xl text-white mb-3">{title}</h2>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function List({ items }) {
    return (
        <ul className="list-disc list-inside space-y-1 ml-2 text-gray-300">
            {items.map((it) => <li key={it}>{it}</li>)}
        </ul>
    );
}
